import { UserCard } from "common/hovercards/UserCard";
import { Icon } from "common/Icon";
import { Details, Result, UserDetails } from "common/search/Result";
import { ResultsGroup } from "common/search/ResultsGroup";
import { commonMessages, featureNames, Msg, privacyLevels, useFormatMessage } from "lib/localization";
import React from "react";
import { defineMessages } from "react-intl";
import { resultTypes } from "../LoadExisting";
import styles from "./SearchResults.scss";

const messages = defineMessages({
  showXMore: {
    id: "SearchResults.showXMore",
    defaultMessage: "Show {resultsRemaining} results",
  },
  yourUploadedFile: {
    id: "SearchResults.yourUploadedFile",
    defaultMessage: "Uploaded Files",
  },
});

const SelectableResult = ({ selected, onClick, ...props }) => (
  <div className={styles.SelectableResult} onClick={onClick}>
    <div className={styles.inner}>
      {selected && (
        <div className={styles.selected}>
          <Icon i="checkmark-circle" size={25} />
        </div>
      )}
      <Result {...props} />
    </div>
  </div>
);

class LoadMoreResultsGroup extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      expanded: false,
    };
  }

  toggleExpanded = () => {
    this.setState({ expanded: !this.state.expanded });
  };

  loadMore = () => {
    const { loadMore, query, resultsGroup } = this.props;

    const offset = resultsGroup.get("records").size;
    const resultsKey = resultsGroup.get("resultsKey", query);
    const type = resultsGroup.get("type");

    return loadMore(query.merge({ offset }), {
      type,
      resultsKey,
      append: true,
    });
  };

  render() {
    const {
      children,
      selectedResults,
      resultsGroup,
      clickResult,
      initialPageSize,
      showEmpty,
      showUsernames,
      ...props
    } = this.props;
    const { expanded } = this.state;

    const records = resultsGroup.get("records");
    const resultsToDisplay = expanded || initialPageSize === -1 ? records : records.take(initialPageSize);
    const resultsRemaining = resultsGroup.get("results_count") - resultsToDisplay.size;

    const showExpandLink = !expanded && resultsToDisplay.size < records.size;
    const moreAvailable = resultsGroup.get("results_count") > resultsToDisplay.size;

    const resultIsSelected = (result) =>
      selectedResults.find(
        (selectedResult) =>
          selectedResult.get("type") === result.get("type") && selectedResult.get("id") === result.get("id")
      );

    return (
      <div className={styles.LoadMoreResultsGroup}>
        <ResultsGroup noPadding headingClassName={styles.heading} {...props}>
          {resultsToDisplay.map((r) => (
            <SelectableResult
              key={r}
              selected={resultIsSelected(r)}
              title={r.get("name")}
              detail={
                <Details
                  distance={r.get("distance")}
                  elevationGain={r.get("elevation_gain")}
                  shortLocation={r.get("short_location")}
                  countryCode={r.get("country_code")}
                  hasCues={r.get("has_course_points")}
                />
              }
              userDetail={
                showUsernames && (
                  <UserCard id={r.get("user_id")} name={r.get("user_name")}>
                    <UserDetails id={r.get("user_id")} name={r.get("user_name")} />
                  </UserCard>
                )
              }
              img={`/${r.get("type")}s/thumb/${r.get("id")}.png`}
              onClick={() => clickResult(r)}
            />
          ))}
          {showEmpty && resultsToDisplay.size === 0 && (
            <div className={styles.empty}>
              <Msg m={commonMessages.noResults} />.
            </div>
          )}
          {showExpandLink && (
            <div className={styles.groupFooter}>
              <a onClick={() => this.toggleExpanded()}>
                <Msg m={messages.showXMore} values={{ resultsRemaining }} />
              </a>
            </div>
          )}
          {moreAvailable && !showExpandLink && (
            <div className={styles.groupFooter}>
              <a onClick={() => this.loadMore()}>
                <Msg m={messages.showXMore} values={{ resultsRemaining }} />
              </a>
            </div>
          )}
        </ResultsGroup>
      </div>
    );
  }
}

export class SearchResults extends React.PureComponent {
  getGroupTitle = (type, userId) => {
    const { currentUser, routesOnly, intl } = this.props;
    const club = type === resultTypes.CLUB && currentUser.clubs.get("records").find((c) => c.get("user_id") === userId);

    return (
      <React.Fragment>
        {type === resultTypes.PUBLIC && <Msg m={privacyLevels.public} />}
        {type === resultTypes.USER && (
          <Msg
            m={commonMessages.your}
            values={{
              things: intl.formatMessage(routesOnly ? featureNames.routes : featureNames.routesAndRides),
            }}
          />
        )}
        {type === resultTypes.CLUB && club && club.get("name")}
      </React.Fragment>
    );
  };

  render() {
    const { results, files, removeFile, selectedResults, clickResult, loadMore } = this.props;

    const sortedResults = results && results.entrySeq().sortBy(([query, rg]) => rg.get("type"));

    // Get the number of results from clubs and public searches
    const nonUserResultsCount =
      results &&
      results
        .entrySeq()
        .filter(([q, rg]) => rg.get("type") !== resultTypes.USER)
        .reduce((sum, [q, rg]) => sum + rg.get("results_count"), 0);

    return (
      <div className={styles.SearchResults}>
        {files?.size > 0 && <FileGroup files={files} removeFile={removeFile} />}
        {sortedResults &&
          sortedResults.map(
            ([query, resultsGroup]) =>
              (resultsGroup.get("records").size > 0 || resultsGroup.get("type") === resultTypes.USER) && (
                <LoadMoreResultsGroup
                  key={query}
                  query={query}
                  resultsGroup={resultsGroup}
                  loadMore={loadMore}
                  selectedResults={selectedResults}
                  clickResult={clickResult}
                  showEmpty={resultsGroup.get("type") === resultTypes.USER}
                  showUsernames={resultsGroup.get("type") === resultTypes.PUBLIC}
                  initialPageSize={resultsGroup.get("type") === resultTypes.USER && nonUserResultsCount === 0 ? -1 : 3}
                  title={this.getGroupTitle(resultsGroup.get("type"), query.get("user_id"))}
                />
              )
          )}
      </div>
    );
  }
}

const FileGroup = ({ files, removeFile }) => {
  const fm = useFormatMessage();
  return (
    <div className={styles.fileGroup}>
      <div className={styles.heading} data-heading-alt>
        {fm(messages.yourUploadedFile)}
      </div>
      {files.toSeq().map((file, idx) => (
        <SelectableResult key={idx} selected onClick={() => removeFile(idx)} title={file.filename} />
      ))}
    </div>
  );
};
